import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { message, Modal } from "antd";
import * as positionServices from "features/company/services/positionApi";
import * as employeeServices from "features/employee/services/employeeApi";
import useOptions from "features/masterData/hooks/useOptions";
import { generateRandomString } from "utils/helper";
import {
  WELFARE_CALCULATE_TYPE,
  wefareLimitDefault
} from "../schemas/welfareSchema";
import * as incomeSerices from "../service/incomeDeductionApi";
import * as services from "../service/welfareApi";
import { setCurrentWelfare, setIsLoading } from "../slices/payrollSlice";
import { useForm } from "antd/es/form/Form";
import { setIsLoadingWelfare, setWelfareWorkflow } from "../slices/welfareSlice";

function toParams(params) {
  const modifiedParams = {
    stpWelfareId: params?.stpWelfareId,
    companyId: params?.companyId,
    welfareName: params?.welfareName,
    welfareNameEn: params?.welfareNameEn,
    categoryId: params?.categoryId,
    subcategoryId: params?.subcategoryId,
    welfareTypeId: params?.welfareTypeId,
    incomeOrDeduct: params?.incomeOrDeduct,
    incomeDeductionId: params?.incomeDeductionId,
    recipient: params?.recipient,
    description: params?.description,
    employeeList: params?.employeeList || [],
    positionList: params?.positionList || [],
    employeeTypeList: params?.employeeTypeList || [],
    ignoreEmployees: params?.ignoreEmployees || [],
    year: params?.year,
    month: params?.month,
    genderId: params?.genderId,
    calculateTypeId: params?.calculateTypeId,

    calculateWithPayroll: params?.calculateWithPayroll ? "Y" : "N",
    individualFlag: params?.individualFlag ? "Y" : "N",
    positionFlag: params?.positionFlag ? "Y" : "N",
    yearOfService: params?.yearOfService ? "Y" : "N",
    genderFlag: params?.genderFlag ? "Y" : "N",
    passedProbationFlag: params?.passedProbationFlag ? "Y" : "N",
    attachmentFlag: params?.attachmentFlag ? "Y" : "N",
    welfareLimitFlag: params?.welfareLimitFlag ? "Y" : "N",
    multipleCreditLimitFlag: params?.multipleCreditLimitFlag ? "Y" : "N",
    allEmployeeFlag: params?.allEmployeeFlag ? "Y" : "N",
    conditionFlag: params?.conditionFlag ? "Y" : "N",
    employeeTypeFlag: params?.employeeTypeFlag ? "Y" : "N",
    sameDayFlag: params?.sameDayFlag ? "Y" : "N"
  };

  const isWelfareLimitFlag = modifiedParams.welfareLimitFlag === "Y";
  const isMultiCredit = modifiedParams.multipleCreditLimitFlag === "Y";
  const isPositionFlag = modifiedParams.positionFlag === "Y";
  const isEmployeeFlag = modifiedParams.individualFlag === "Y";
  const isConditionFlag = modifiedParams.conditionFlag === "Y";
  const isAllEmployee = modifiedParams.allEmployeeFlag === "Y";

  if (isMultiCredit) {
    modifiedParams.multipleCreditLimitForEmployee = isEmployeeFlag
      ? (params?.multipleCreditLimitForEmployee || []).map((item) => {
        const id = item.employeeId;
        return {
          position: null,
          employeeId: id,
          maxPerMonth:
            params[`numberOfRightPerMonth${id}`] ??
            item.maxPerMonth ??
            null,
          maxPerYear:
            params[`numberOfRightPerYear${id}`] ??
            item.maxPerYear ??
            null,
          percentageOfCreditLimitReceived:
            params[`percentageOfCreditLimitReceived${id}`] ??
            item.percentageOfCreditLimitReceived ??
            null,
          maximumWithdrawalIsNotMoreThanTheBaseSalary:
            params[`maximumWithdrawNotLessThan${id}`] ??
            item.maximumWithdrawalIsNotMoreThanTheBaseSalary ??
            null,
          maxPerRequest:
            params[`numberOfRightPerTime${id}`] ??
            item.maxPerRequest ??
            null,
        };
      })
      : [];

    modifiedParams.multipleCreditLimitForPosition = isPositionFlag
      ? (params?.multipleCreditLimitForPosition || []).map((item) => {
        const id = item.positionId;
        return {
          positionId: id,
          employee: null,
          maxPerMonth:
            params[`numberOfRightPerMonth${id}`] ??
            item.maxPerMonth ??
            null,
          maxPerYear:
            params[`numberOfRightPerYear${id}`] ??
            item.maxPerYear ??
            null,
          percentageOfCreditLimitReceived:
            params[`percentageOfCreditLimitReceived${id}`] ??
            item.percentageOfCreditLimitReceived ??
            null,
          maximumWithdrawalIsNotMoreThanTheBaseSalary:
            params[`maximumWithdrawNotLessThan${id}`] ??
            item.maximumWithdrawalIsNotMoreThanTheBaseSalary ??
            null,
          maxPerRequest:
            params[`numberOfRightPerTime${id}`] ??
            item.maxPerRequest ??
            null,
        };
      })
      : [];

    modifiedParams.multipleCreditLimitForGeneral =
      (isConditionFlag && !isPositionFlag)
        ? [
          {
            maxPerMonth: params?.multipleCreditLimitForGeneral?.numberOfRightPerMonth ?? null,
            maxPerYear: params?.multipleCreditLimitForGeneral?.numberOfRightPerYear ?? null,
            maxPerRequest: params?.multipleCreditLimitForGeneral?.numberOfRightPerTime ?? null,
            maximumWithdrawalIsNotMoreThanTheBaseSalary: params?.multipleCreditLimitForGeneral?.maximumWithdrawNotLessThan ?? null,
            percentageOfCreditLimitReceived: params?.multipleCreditLimitForGeneral?.percentageOfCreditLimitReceived ?? null,
          }
        ]
        : [];

    modifiedParams.multipleCreditLimit = [];
  } else {
    modifiedParams.multipleCreditLimit = [];
    modifiedParams.multipleCreditLimitForEmployee = [];
    modifiedParams.multipleCreditLimitForPosition = [];
    modifiedParams.multipleCreditLimitForGeneral = [];
  }

  if (isAllEmployee) {
    modifiedParams.multipleCreditLimit = [
      {
        maxPerMonth: params.numberOfRightPerMonth ?? null,
        maxPerYear: params.numberOfRightPerYear ?? null,
        maxPerRequest: params.numberOfRightPerTime ?? null,
        maximumWithdrawalIsNotMoreThanTheBaseSalary: params.maximumWithdrawNotLessThan ?? null,
        percentageOfCreditLimitReceived: params.percentageOfCreditLimitReceived ?? null,
      }
    ];
    modifiedParams.multipleCreditLimitForGeneral = [];
  }

  if (!isEmployeeFlag && !isPositionFlag && !isWelfareLimitFlag) {
    modifiedParams.multipleCreditLimit = [];
    modifiedParams.multipleCreditLimitForEmployee = [];
    modifiedParams.multipleCreditLimitForPosition = [];
    modifiedParams.multipleCreditLimitForGeneral = [];
  }

  if (isWelfareLimitFlag && !isMultiCredit) {
    modifiedParams.multipleCreditLimit = [
      {
        maxPerMonth: params.numberOfRightPerMonth ?? null,
        maxPerYear: params.numberOfRightPerYear ?? null,
        maxPerRequest: params.numberOfRightPerTime ?? null,
        maximumWithdrawalIsNotMoreThanTheBaseSalary: params.maximumWithdrawNotLessThan ?? null,
        percentageOfCreditLimitReceived: params.percentageOfCreditLimitReceived ?? null,
      }
    ];
  }

  modifiedParams.multipleCreditLimit =
    modifiedParams.multipleCreditLimit?.length > 0
      ? modifiedParams.multipleCreditLimit
      : [];
  modifiedParams.multipleCreditLimitForEmployee =
    modifiedParams.multipleCreditLimitForEmployee?.length > 0
      ? modifiedParams.multipleCreditLimitForEmployee
      : [];
  modifiedParams.multipleCreditLimitForPosition =
    modifiedParams.multipleCreditLimitForPosition?.length > 0
      ? modifiedParams.multipleCreditLimitForPosition
      : [];
  modifiedParams.multipleCreditLimitForGeneral =
    modifiedParams.multipleCreditLimitForGeneral?.length > 0
      ? modifiedParams.multipleCreditLimitForGeneral
      : [];

  return modifiedParams;
}


function useWelfare() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const [form] = useForm();
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const currentEmployee = useSelector((state) => state.company.currentEmployee);
  const isLoading = useSelector((state) => state.payroll.isLoading);
  const currentWelfare = useSelector((state) => state.payroll.currentWelfare || {});
  const [positionOptions, setPositionOptions] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [incomeOptions, setIncomeOptions] = useState([]);
  const [welfareCategory, setWelfareCategory] = useState([]);
  const [welfareSubTypeOptions, setWelfareSubTypeOptions] = useState([]);
  const [recipientOptions, setRecipientOptions] = useState([]);
  const [employeeByPositionOptions, setEmployeeByPositionOptions] = useState([]);
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);
  const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
  const [isEmployeeTypeModalOpen, setIsEmployeeTypeModalOpen] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [selectedEmployeeType, setSelectedEmployeeType] = useState([]);
  const [incomeOpt, setIncomeOpt] = useState([]);
  const [deductionOpt, setDeductionOpt] = useState([]);

  const {
    welfareTypeOptions = [],
    employeeTypeOptions = [],
    periodTimeUnitOptions = [],
    genderOptions = [],
    welfareLimitOptions = [],
    welfarePaidOptions = [],
    getWelfareTypeOptions,
    getEmployeeTypeOptions,
    getPeriodTimeUnitOptions,
    getGenderOptions,
    getWelfareLimitOptions,
    getWelfarePaidOptions,
  } = useOptions();

  function toInitialValues(initialValues = {}) {
    if (!initialValues || Object.keys(initialValues).length === 0) return {};

    const generalLimit = initialValues?.multipleCreditLimitForGeneral?.[0];

    return {
      id: initialValues?.id,
      welfareName: initialValues?.welfareName,
      welfareNameEn: initialValues?.welfareNameEn,
      categoryId: initialValues?.category?.id,
      subcategoryId: initialValues?.subcategory?.id,
      welfareTypeId: initialValues?.welfareType?.id,
      incomeOrDeduct: initialValues?.incomeOrDeduct,
      recipient: "68f0aef783032723d4b5bc0c",
      incomeDeductionId: initialValues?.incomeDeduction?.id,
      calculateWithPayroll: initialValues?.calculateWithPayroll === "Y",
      description: initialValues?.description,
      individualFlag: initialValues?.individualFlag === "Y",
      positionFlag: initialValues?.positionFlag === "Y",
      yearOfService: initialValues?.yearOfService === "Y",
      year: initialValues?.year,
      month: initialValues?.month,
      genderFlag: initialValues?.genderFlag === "Y",
      genderId: initialValues?.gender?.id,
      passedProbationFlag: initialValues?.passedProbationFlag === "Y",
      attachmentFlag: initialValues?.attachmentFlag === "Y",

      welfareLimitFlag: initialValues?.welfareLimitFlag === "Y",
      allEmployeeFlag: initialValues?.allEmployeeFlag === "Y",
      conditionFlag: initialValues?.conditionFlag === "Y",
      calculateTypeId: initialValues?.calculateType?.id,
      multipleCreditLimitFlag: initialValues?.multipleCreditLimitFlag === "Y",
      employeeTypeFlag: initialValues?.employeeTypeFlag === "Y",
      sameDayFlag: initialValues?.sameDayFlag === "Y",

      multipleCreditLimitForEmployee: (initialValues?.multipleCreditLimitForEmployee || [])?.map((item) => ({
        employeeId: item?.employee?.id,
        employeeName: item?.employee?.fullname,
        employeeNameEn: item?.employee?.fullnameEn,
        numberOfRightPerMonth: item?.maxPerMonth,
        numberOfRightPerYear: item?.maxPerYear,
        numberOfRightPerTime: item?.maxPerRequest,
        maximumWithdrawNotLessThan: item?.maximumWithdrawalIsNotMoreThanTheBaseSalary,
        percentageOfCreditLimitReceived: item?.percentageOfCreditLimitReceived,
      })),

      multipleCreditLimitForPosition: (initialValues?.multipleCreditLimitForPosition || [])?.map((item) => ({
        positionId: item?.position?.id,
        positionName: item?.position?.positionName,
        positionNameEn: item?.position?.positionNameEn,
        numberOfRightPerMonth: item?.maxPerMonth,
        numberOfRightPerYear: item?.maxPerYear,
        numberOfRightPerTime: item?.maxPerRequest,
        maximumWithdrawNotLessThan: item?.maximumWithdrawalIsNotMoreThanTheBaseSalary,
        percentageOfCreditLimitReceived: item?.percentageOfCreditLimitReceived,
      })),

      multipleCreditLimit: (initialValues?.multipleCreditLimit || [])?.map((item) => ({
        numberOfRightPerMonth: item?.maxPerMonth,
        numberOfRightPerYear: item?.maxPerYear,
        numberOfRightPerTime: item?.maxPerRequest,
        maximumWithdrawNotLessThan: item?.maximumWithdrawalIsNotMoreThanTheBaseSalary,
        percentageOfCreditLimitReceived: item?.percentageOfCreditLimitReceived,
      })),

      generalNumberOfRightPerMonth: generalLimit?.maxPerMonth,
      generalNumberOfRightPerYear: generalLimit?.maxPerYear,
      generalNumberOfRightPerTime: generalLimit?.maxPerRequest,
      generalMaximumWithdrawNotLessThan: generalLimit?.maximumWithdrawalIsNotMoreThanTheBaseSalary,
      generalPercentageOfCreditLimitReceived: generalLimit?.percentageOfCreditLimitReceived,

      employeeList: initialValues?.employeeList || [],
      positionList: initialValues?.positionList || [],
      employeeTypeList: initialValues?.employeeTypeList || [],
      ignoreEmployees: (initialValues?.employeeIgnoreList || []).map(emp => emp.id)
    };
  }

  const initialValues = useMemo(() => {
    return toInitialValues(currentWelfare);
  }, [currentWelfare]);

  const modifiedPositionOptions = useMemo(() => {
    const modifiedOptions = positionOptions.map((opt) => ({
      ...opt,
      label: i18n.language === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [i18n, positionOptions]);

  const modifiedEmployeeOptions = useMemo(() => {
    const modifiedOptions = employeeOptions.map((opt) => ({
      ...opt,
      label: i18n.language === "en" ? opt.fullNameEn : opt.fullName,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [i18n, employeeOptions]);

  const modifiedEmployeeTypeOptions = useMemo(() => {
    const modifiedOptions = employeeTypeOptions.map((opt) => ({
      ...opt,
      label: i18n.language === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [i18n, employeeTypeOptions]);

  const modifiedIncomeOptions = useMemo(() => {
    const modifiedOptions = incomeOptions.map((opt) => ({
      ...opt,
      label: i18n.language === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [i18n, incomeOptions]);

  const modifiedWelfareCategoryOptions = useMemo(() => {
    const modifiedOptions = welfareCategory.map((opt) => ({
      ...opt,
      label: i18n.language === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [i18n.language, welfareCategory]);

  const modifiedWelfareSubTypeOptions = useMemo(() => {
    const modifiedOptions = welfareSubTypeOptions.map((opt) => ({
      ...opt,
      label: i18n.language === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [i18n.language, welfareSubTypeOptions]);

  const modifiedRecipientOptions = useMemo(() => {
    const modifiedOptions = recipientOptions.map((opt) => ({
      ...opt,
      label: i18n.language === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [i18n.language, recipientOptions]);

  const modifiedEmployeeByPositionOptions = useMemo(() => {
    const modifiedOptions = employeeByPositionOptions.map((opt) => ({
      // ...opt,
      label: i18n.language === "en" ? opt.nameAndSurnameEn : opt.nameAndSurname,
      value: opt.employeeId,
    }));
    return modifiedOptions;
  }, [employeeByPositionOptions, i18n.language]);

  const modifiedIncomeOpt = useMemo(() => {
    const modifiedOptions = incomeOpt.map((opt) => ({
      label: i18n.language === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [incomeOpt, i18n.language]);

  const modifiedDeductOpt = useMemo(() => {
    const modifiedOptions = deductionOpt.map((opt) => ({
      label: i18n.language === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [deductionOpt, i18n.language]);

  const getWorkFlowByTypeForWelfare = useCallback(
    async (workFlowType) => {
      try {
        dispatch(setIsLoadingWelfare(true));
        const response = await services.getWorkFlowByTypeWelfare({
          workFlowType,
          companyId: currentCompany,
          requestId: generateRandomString(),
          workFlowGroup: "WELFARE"
        });

        dispatch(setWelfareWorkflow(response.data?.content))

      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoadingWelfare(false));
      }
    },
    [currentCompany, dispatch]
  );

  const getWelfare = useCallback(
    async (welfareId) => {
      try {
        dispatch(setIsLoading(true));
        const response = await services.getWelfare({
          welfareId,
          requestId: generateRandomString(),
          companyId: currentCompany,
        });

        const welfareData = response.data || {};
        dispatch(setCurrentWelfare(welfareData));

        if (welfareData && welfareData?.welfareRunningCode) {
          await getWorkFlowByTypeForWelfare(welfareData?.welfareRunningCode);
        }

      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch, getWorkFlowByTypeForWelfare]
  );

  const onCancel = useCallback(() => {
    navigate("/configuration/payroll", { state: { selectedTab: 'welfare' } });
  }, [navigate]);
  const handleCancel = useCallback(() => {
    Modal.confirm({
      title: t("dialog.confirmation.header"),
      content:
        i18n.language === "en"
          ? "Are you sure you want to cancel the changes?"
          : "คุณต้องการยกเลิกการแก้ไขใช่หรือไม่ ?",
      onOk() {
        onCancel();
      },
      okText: t("common.confirm"),
      cancelText: t("common.cancel"),
    });
  }, [onCancel, t, i18n.language]);


  const createWelfare = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        const result = await services.createWelfare(toParams({ ...values }));
        message.success(t("common.success"))
        navigate("/configuration/payroll", { state: { selectedTab: 'welfare' } });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [dispatch, navigate]
  );

  const updateWelfare = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        await services.updateWelfare(
          toParams({ ...values, stpWelfareId: values.id })
        );
        message.success(t("common.success"))
        navigate("/configuration/payroll", { state: { selectedTab: 'welfare' } });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [dispatch, navigate]
  );

  const onSubmit = useCallback(
    (values) => {
      const modifiedValues = {
        ...values,
        companyId: currentCompany,
      };

      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (i18n.language === "en"
          ? <span>
            Are you sure confirm to {values.id ? "edit" : "create"}{" "}
            welfare ?
          </span>
          : <span>
            คุณยืนยันที่จะ{values.id ? "แก้ไข" : "สร้าง"}สวัสดิการใช่หรือไม่ ?
          </span>
        ),
        onOk() {
          if (values.id) {
            updateWelfare(modifiedValues);
          } else {
            createWelfare(modifiedValues);
          }
        },
        okText: t("common.confirm").toUpperCase(),
        cancelText: t("common.cancel").toUpperCase(),
      });
    },
    [currentCompany, updateWelfare, createWelfare]
  );

  const getPosition = useCallback(async () => {
    if (typeof currentCompany !== "object") {
      try {
        dispatch(setIsLoading(true));
        const values = {
          requestId: generateRandomString(),
          companyId: currentCompany,
        };
        const response = await positionServices.dropdownhPosition(values);
        setPositionOptions(response.data);
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    }
  }, [currentCompany, dispatch]);

  const getEmployeeOptions = useCallback(async () => {
    if (typeof currentCompany !== "object") {
      try {
        dispatch(setIsLoading(true));
        const values = {
          requestId: generateRandomString(),
          companyId: currentCompany,
        };
        const response = await employeeServices.getDropDown(values);
        setEmployeeOptions(response.data);
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    }
  }, [currentCompany, dispatch]);

  const getIncomeDeductionOptions = useCallback(
    async (type) => {
      try {
        dispatch(setIsLoading(true));
        const values = {
          requestId: generateRandomString(),
          companyId: currentCompany,
          type,
        };
        const response = await incomeSerices.getIncomeDeductionByType(values);
        setIncomeOptions(response.data);
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch]
  );

  const getIncomeOptions = useCallback(
    async () => {
      try {
        dispatch(setIsLoading(true));
        const values = {
          requestId: generateRandomString(),
          companyId: currentCompany,
          employeeId: currentEmployee
        };
        const response = await services.getIncomeByCompany(values);
        setIncomeOpt(response.data);
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, currentEmployee, dispatch]
  );

  const getDeductionOptions = useCallback(
    async () => {
      try {
        dispatch(setIsLoading(true));
        const values = {
          requestId: generateRandomString(),
          companyId: currentCompany,
          employeeId: currentEmployee
        };
        const response = await services.getDeductionByCompanyId(values);
        setDeductionOpt(response.data);
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, currentEmployee, dispatch]
  );

  const getWelfareCategoryOptions = useCallback(
    async (type) => {
      try {
        dispatch(setIsLoading(true));
        const values = {
          requestId: generateRandomString(),
          companyId: currentCompany,
          employeeId: currentEmployee,
          groupCode: "WELFARE_CATEGORY"
        };
        const response = await services.getWelfareCategory(values);
        setWelfareCategory(response.data?.content);
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, currentEmployee, dispatch]
  );

  const getWelfareSubTypeOptions = useCallback(
    async () => {
      try {
        dispatch(setIsLoading(true));
        const values = {
          requestId: generateRandomString(),
          companyId: currentCompany,
          employeeId: currentEmployee,
          code: "WELFARE_SUBCATEGORY"
        };
        const response = await services.getWelfareSubType(values);
        setWelfareSubTypeOptions(response.data?.content);
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, currentEmployee, dispatch]
  );

  const getRecipientOptionsList = useCallback(
    async () => {
      try {
        dispatch(setIsLoading(true));
        const values = {
          requestId: generateRandomString(),
          companyId: currentCompany,
          employeeId: currentEmployee,
          code: "WELFARE_RECIPIENT"
        };
        const response = await services.getRecipient(values);
        setRecipientOptions(response.data?.content);
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, currentEmployee, dispatch]
  );

  const getEmployeeByPositionIdOptions = useCallback(
    async (positionId = [], employeeIds = []) => {
      try {
        dispatch(setIsLoading(true));
        const values = {
          requestId: generateRandomString(),
          companyId: currentCompany,
          employeeId: currentEmployee,
          positionId: positionId,
          employeeSelected: employeeIds
        };
        const response = await services.getEmployeeByPositionIdDropdown(values);
        setEmployeeByPositionOptions(response.data?.content);
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, currentEmployee, dispatch]
  );

  const onCalculateAsChange = useCallback(
    (type) => {
      getIncomeDeductionOptions(type);
    },
    [getIncomeDeductionOptions]
  );

  const handleAddPositionIds = useCallback(
    async (newPositionIds = [], employeeIds = []) => {
      const allPositionIds = [
        ...(selectedPositions?.map((p) => p.id) || []),
        ...newPositionIds,
      ];

      await getEmployeeByPositionIdOptions(allPositionIds, employeeIds);
    },
    [getEmployeeByPositionIdOptions, selectedPositions]
  );

  useEffect(() => {
    if (typeof currentCompany !== "object" && params.id) {
      getWelfare(params.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany]);

  useEffect(() => {
    getWelfareTypeOptions();
  }, [getWelfareTypeOptions]);

  useEffect(() => {
    if (currentCompany !== null) {
      getEmployeeTypeOptions({
        companyId: currentCompany,
        groupType: "EMPLOYEE_TYPE",
      });
    }
  }, [currentCompany, getEmployeeTypeOptions]);

  useEffect(() => {
    getPosition();
  }, [getPosition]);

  useEffect(() => {
    getPeriodTimeUnitOptions();
  }, [getPeriodTimeUnitOptions]);

  useEffect(() => {
    getGenderOptions();
  }, [getGenderOptions]);

  useEffect(() => {
    getEmployeeOptions();
  }, [getEmployeeOptions]);

  useEffect(() => {
    getWelfareLimitOptions();
  }, [getWelfareLimitOptions]);

  useEffect(() => {
    getWelfarePaidOptions();
  }, [getWelfarePaidOptions]);

  useEffect(() => {
    getWelfareCategoryOptions();
    getWelfareSubTypeOptions();
    getRecipientOptionsList();
    getDeductionOptions();
    getIncomeOptions();
  }, [getWelfareCategoryOptions, getWelfareSubTypeOptions, getRecipientOptionsList, getDeductionOptions, getIncomeOptions]);

  // useEffect(() => {
  //   if (typeof currentCompany !== "object") {
  //     getIncomeDeductionOptions(WELFARE_CALCULATE_TYPE.INCOME);
  //   }
  // }, [currentCompany, getIncomeDeductionOptions]);

  useEffect(() => {
    return () => {
      dispatch(setCurrentWelfare({}));
    };
  }, [dispatch]);

  useEffect(() => {
    if (currentWelfare?.employeeList) {
      setSelectedEmployees(currentWelfare.employeeList);
    }
    if (currentWelfare?.positionList) {
      setSelectedPositions(currentWelfare.positionList);
    }
    if (currentWelfare?.employeeTypeList) {
      setSelectedEmployeeType(currentWelfare.employeeTypeList);
    }
  }, [currentWelfare]);

  const handleSaveEmployees = useCallback((employees) => {
    setSelectedEmployees(employees);
    setIsEmployeeModalOpen(false);
  }, []);

  const handleSaveEmployeeType = useCallback((employeeType) => {
    setSelectedEmployeeType(employeeType);
    setIsEmployeeTypeModalOpen(false);
  }, []);

  const handleSavePositions = useCallback((positions, employeeIgnoreList = []) => {
    setSelectedPositions(positions);
    form.setFieldValue("employeeIgnoreList", employeeIgnoreList);
    setIsPositionModalOpen(false);
  }, [form]);

  useEffect(() => {
    if (isPositionModalOpen) {
      const hasEmployees = selectedEmployees?.length > 0;
      if (hasEmployees) {
        const employeeIds = selectedEmployees.map((e) => e.id);
        const allPositionIds = selectedPositions.map((p) => p.id);
        getEmployeeByPositionIdOptions(allPositionIds, employeeIds);
      }
    }
  }, [
    isPositionModalOpen,
    selectedEmployees,
    selectedPositions,
    getEmployeeByPositionIdOptions,
  ]);

  return {
    isLoading,
    initialValues,
    welfareTypeOptions,
    employeeTypeOptions: modifiedEmployeeTypeOptions,
    positionOptions: modifiedPositionOptions,
    periodTimeUnitOptions,
    genderOptions,
    employeeOptions: modifiedEmployeeOptions,
    welfareLimitOptions,
    welfarePaidOptions,
    isEmployeeModalOpen,
    setIsEmployeeModalOpen,
    setIsPositionModalOpen,
    isPositionModalOpen,
    onSubmit,
    onCancel,
    handleCancel,
    selectedEmployees,
    selectedPositions,
    handleSaveEmployees,
    handleSavePositions,
    welfarecategoryOptions: modifiedWelfareCategoryOptions,
    welfareSubTypeOptions: modifiedWelfareSubTypeOptions,
    recipientOptions: modifiedRecipientOptions,
    employeeByPositionOptions: modifiedEmployeeByPositionOptions,
    handleAddPositionIds,
    getDeductionOptions,
    getIncomeOptions,
    deductionOpt: modifiedDeductOpt,
    incomeOpt: modifiedIncomeOpt,
    getWorkFlowByTypeForWelfare,
    isEmployeeTypeModalOpen,
    setIsEmployeeTypeModalOpen,
    selectedEmployeeType,
    handleSaveEmployeeType
  };
}
export default useWelfare;

